import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { CouponModel } from '../../../../../models/coupon.model';
import { AlertService } from '../../../../../services/alert.service';
import { CouponService } from '../../../../../services/coupon.service';
import { ConfirmDialogComponent } from '../../../../shared-components/confirm-dialog/confirm-dialog.component';
import { CouponFormularComponent } from '../../../../shared-components/coupon-formular/coupon-formular.component';
import { GenerateCouponCodesComponent } from '../../../../shared-components/generate-coupon-codes/generate-coupon-codes.component';

@Component({
  selector: 'app-sellable-codes',
  templateUrl: './sellable-codes.component.html',
  styleUrl: './sellable-codes.component.scss',
  standalone: false,
})
export class SellableCodesComponent {
  public isLoading = true;
  public sellableCodes: CouponModel[] = [];

  constructor(
    private couponService: CouponService,
    private dialogService: DialogService,
    private alertService: AlertService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.getSellableCodes();
  }

  private getSellableCodes(): void {
    this.isLoading = true;
    this.couponService
      .getAllSellableCoupons(false)
      .pipe(take(1))
      .subscribe({
        next: sellableCodes => {
          this.sellableCodes = sellableCodes;
          this.isLoading = false;
        },
        error: error => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  public onCouponActiveStateChanged(enabled: boolean, couponId: number): void {
    if (enabled) {
      this.enableCoupon(couponId);
    } else {
      this.disableCoupon(couponId);
    }
  }

  private enableCoupon(couponId: number): void {
    this.couponService.enableCoupon(couponId).subscribe({
      next: () => {
        this.sellableCodes.find(coupon => coupon.id === couponId).isActive =
          true;
        this.alertService.showSuccessAlert('Gutschein erfolgreich aktiviert');
      },
      error: error => {
        this.sellableCodes.find(coupon => coupon.id === couponId).isActive =
          false;
        console.error('Failed to enable coupon', error);
        this.alertService.showErrorAlert(
          'Beim Aktivieren des Gutscheins ist ein Fehler aufgetreten'
        );
      },
    });
  }

  private disableCoupon(couponId: number): void {
    this.couponService.disableCoupon(couponId).subscribe({
      next: () => {
        this.sellableCodes.find(coupon => coupon.id === couponId).isActive =
          false;
        this.alertService.showSuccessAlert('Gutschein erfolgreich deaktiviert');
      },
      error: error => {
        this.sellableCodes.find(coupon => coupon.id === couponId).isActive =
          true;
        console.error('Failed to disable coupon', error);
        this.alertService.showErrorAlert(
          'Beim Aktivieren des Gutscheins ist ein Fehler aufgetreten'
        );
      },
    });
  }

  public onCouponDeleted(coupon: CouponModel): void {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Verkaufbaren Gutschein löschen',
        message:
          'Bist du sicher, dass du den Gutschein löschen möchten? Nach Entfernen des Gutscheins wird er nicht mehr zum Verkauf verfügbar sein.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
        confirmButtonIcon: 'fa-regular fa-trash-can',
        cancelButtonIcon: 'fa-regular fa-times',
      },
    });
    dialogRef.onClose.pipe(take(1)).subscribe((response: boolean) => {
      if (!response) {
        return;
      }
      this.couponService
        .deleteCoupon(coupon.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.sellableCodes = this.sellableCodes.filter(
              sellableCode => sellableCode.id !== coupon.id
            );
            this.alertService.showSuccessAlert(
              'Gutschein erfolgreich gelöscht'
            );
          },
          error: error => {
            console.error('Failed to delete coupon', error);
            this.alertService.showErrorAlert(
              'Beim Löschen des Gutscheins ist ein Fehler aufgetreten'
            );
          },
        });
    });
  }

  public addSellableCode() {
    const dialogRef = this.dialogService.open(CouponFormularComponent, {
      width: '600px',
      data: {
        type: 'sellable',
      },
    });
    dialogRef.onClose.subscribe((response: boolean) => {
      if (response) {
        this.getSellableCodes();
      }
    });
  }

  public onGenerateCouponCodes() {
    const dialogRef = this.dialogService.open(GenerateCouponCodesComponent, {
      width: '600px',
    });
    dialogRef.onClose.subscribe((response: boolean) => {
      if (response) {
        this.router.navigate([], {
          queryParams: {
            tab: 'coupon-usage',
          },
        });
      }
    });
  }
}
